import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, Copy, Send, ArrowLeft, Check, AlertCircle, MessageSquare } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { BrowserProvider, formatEther, parseEther } from 'ethers';

// Definisi tipe untuk MetaMask
declare global {
  interface Window {
    ethereum?: any;
  }
}

export default function WalletDemo() {
  const [connected, setConnected] = useState(false);
  const [copied, setCopied] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [balance, setBalance] = useState('0.0000');
  const [chainId, setChainId] = useState('');
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false);
  const [provider, setProvider] = useState<BrowserProvider | null>(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [messageToSign, setMessageToSign] = useState('Saya memverifikasi kepemilikan wallet ini di portfolio Mahin Utsman Nawawi');
  const [signedMessage, setSignedMessage] = useState('');
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendAddress, setSendAddress] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  
  // Demo transactions (tetap ada untuk showcase)
  const transactions = [
    { id: '1', type: 'receive', amount: 0.5, from: '0x1234...5678', date: '2025-11-05' },
    { id: '2', type: 'send', amount: 0.2, to: '0xabcd...efgh', date: '2025-11-04' },
    { id: '3', type: 'receive', amount: 1.0, from: '0x9876...5432', date: '2025-11-03' },
  ];

  // Check MetaMask Installation
  useEffect(() => {
    if (typeof window.ethereum !== 'undefined') {
      setIsMetaMaskInstalled(true);
      
      // Listen untuk account changes
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      
      // Cleanup listeners
      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, []);

  const handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      // User disconnected
      disconnectWallet();
      toast.error('Wallet terputus dari MetaMask');
    } else if (accounts[0] !== walletAddress) {
      // Account switched
      setWalletAddress(accounts[0]);
      updateBalance(accounts[0]);
      toast.success('Akun berganti: ' + shortenAddress(accounts[0]));
    }
  };

  const handleChainChanged = () => {
    // Reload ketika network berubah
    window.location.reload();
  };

  const connectWallet = async () => {
    if (!isMetaMaskInstalled) {
      toast.error('MetaMask tidak terdeteksi! Silakan install MetaMask extension terlebih dahulu.');
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    try {
      // Request akses ke accounts
      const browserProvider = new BrowserProvider(window.ethereum);
      const accounts = await browserProvider.send('eth_requestAccounts', []);
      
      if (accounts.length > 0) {
        const address = accounts[0];
        setWalletAddress(address);
        setProvider(browserProvider);
        setConnected(true);
        
        // Get balance
        await updateBalance(address);
        
        // Get chainId
        const network = await browserProvider.getNetwork();
        setChainId(network.chainId.toString());
        
        toast.success('Wallet berhasil terhubung! 🎉');
      }
    } catch (error: any) {
      console.error('Error connecting wallet:', error);
      if (error.code === 4001) {
        toast.error('Koneksi ditolak oleh user');
      } else {
        toast.error('Gagal menghubungkan wallet: ' + error.message);
      }
    }
  };

  const updateBalance = async (address: string) => {
    try {
      if (!provider) return;
      
      const balanceWei = await provider.getBalance(address);
      const balanceEth = formatEther(balanceWei);
      setBalance(parseFloat(balanceEth).toFixed(4));
    } catch (error) {
      console.error('Error fetching balance:', error);
      toast.error('Gagal mengambil saldo wallet');
    }
  };

  const disconnectWallet = () => {
    setConnected(false);
    setWalletAddress('');
    setBalance('0.0000');
    setChainId('');
    setProvider(null);
    setSignedMessage('');
    toast.success('Wallet terputus');
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    toast.success('Alamat wallet disalin!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shortenAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getNetworkName = (chainId: string): string => {
    const networks: { [key: string]: string } = {
      '1': 'Ethereum Mainnet',
      '5': 'Goerli Testnet',
      '11155111': 'Sepolia Testnet',
      '137': 'Polygon Mainnet',
      '80001': 'Mumbai Testnet',
      '56': 'BSC Mainnet',
      '97': 'BSC Testnet',
    };
    return networks[chainId] || `Chain ID: ${chainId}`;
  };

  const signMessage = async () => {
    if (!provider || !walletAddress) {
      toast.error('Wallet tidak terhubung');
      return;
    }

    try {
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(messageToSign);
      setSignedMessage(signature);
      toast.success('Pesan berhasil ditandatangani! ✍️');
    } catch (error: any) {
      console.error('Error signing message:', error);
      if (error.code === 4001) {
        toast.error('Penandatanganan ditolak oleh user');
      } else {
        toast.error('Gagal menandatangani pesan: ' + error.message);
      }
    }
  };

  const sendTransaction = async () => {
    if (!provider || !walletAddress) {
      toast.error('Wallet tidak terhubung');
      return;
    }

    if (!sendAddress || !sendAmount) {
      toast.error('Mohon isi alamat dan jumlah');
      return;
    }

    try {
      const signer = await provider.getSigner();
      const tx = await signer.sendTransaction({
        to: sendAddress,
        value: parseEther(sendAmount),
      });

      toast.loading('Transaksi sedang diproses...', { duration: 2000 });
      
      await tx.wait();
      
      toast.success('Transaksi berhasil! 🎉');
      setSendModalOpen(false);
      setSendAddress('');
      setSendAmount('');
      
      // Update balance
      await updateBalance(walletAddress);
    } catch (error: any) {
      console.error('Error sending transaction:', error);
      if (error.code === 4001) {
        toast.error('Transaksi ditolak oleh user');
      } else if (error.code === 'INSUFFICIENT_FUNDS') {
        toast.error('Saldo tidak mencukupi');
      } else {
        toast.error('Gagal mengirim transaksi: ' + error.message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-dark p-4">
      <Toaster position="top-right" />
      
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="glass-card p-4 mb-6 flex items-center gap-4">
          <a href="/#projects" className="text-neon-blue hover:text-neon-green transition">
            <ArrowLeft size={24} />
          </a>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">Web3 Wallet</h1>
            <p className="text-sm text-gray-400">Real MetaMask Integration - Connect Your Wallet</p>
          </div>
          {isMetaMaskInstalled ? (
            <div className="flex items-center gap-2 bg-neon-green/20 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-neon-green rounded-full animate-pulse"></div>
              <span className="text-neon-green text-xs font-semibold">MetaMask Ready</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1 rounded-full">
              <AlertCircle size={14} className="text-red-500" />
              <span className="text-red-500 text-xs font-semibold">MetaMask Not Found</span>
            </div>
          )}
        </div>

        {!connected ? (
          /* Connect Screen */
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-12 text-center"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-neon-blue/20 flex items-center justify-center">
              <Wallet size={48} className="text-neon-blue" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              Hubungkan Wallet Anda
            </h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Terhubung ke MetaMask untuk melihat saldo real, kirim transaksi, dan tanda tangani pesan di blockchain.
            </p>
            
            {!isMetaMaskInstalled && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 max-w-md mx-auto">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <div className="text-left">
                    <p className="text-red-500 font-semibold mb-1">MetaMask Tidak Terdeteksi</p>
                    <p className="text-sm text-gray-400">
                      Untuk menggunakan demo ini, Anda perlu install MetaMask browser extension terlebih dahulu.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={connectWallet}
              className={`text-lg px-8 py-4 flex items-center gap-3 mx-auto ${
                isMetaMaskInstalled 
                  ? 'btn-neon-green' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
              }`}
              disabled={!isMetaMaskInstalled}
            >
              <Wallet size={24} />
              {isMetaMaskInstalled ? 'Connect MetaMask' : 'Install MetaMask First'}
            </motion.button>

            {!isMetaMaskInstalled && (
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://metamask.io/download/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-neon mt-4 text-lg px-8 py-4 flex items-center gap-3 mx-auto w-max"
              >
                Download MetaMask
              </motion.a>
            )}
          </motion.div>
        ) : (
          /* Dashboard */
          <div className="space-y-6">
            {/* Balance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-8 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-neon-blue/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <p className="text-gray-400 mb-2">Total Saldo Real</p>
                <h2 className="text-5xl font-bold text-white mb-6">
                  {balance} <span className="text-neon-blue text-3xl">ETH</span>
                </h2>
                <div className="flex items-center gap-3 mb-4">
                  <code className="bg-dark-lighter px-4 py-2 rounded-lg text-sm text-gray-300 flex-1 truncate">
                    {walletAddress}
                  </code>
                  <button
                    onClick={copyAddress}
                    className="btn-neon flex items-center gap-2"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                    {copied ? 'Tersalin' : 'Salin'}
                  </button>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setSendModalOpen(true)} 
                    className="btn-neon-green flex items-center gap-2"
                  >
                    <Send size={18} />
                    Kirim ETH
                  </button>
                  <button 
                    onClick={() => setShowSignModal(true)} 
                    className="btn-neon flex items-center gap-2"
                  >
                    <MessageSquare size={18} />
                    Sign Message
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Quick Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6"
              >
                <p className="text-gray-400 text-sm mb-2">Nilai USD (estimasi)</p>
                <p className="text-2xl font-bold text-neon-green">
                  ${(parseFloat(balance) * 3500).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card p-6"
              >
                <p className="text-gray-400 text-sm mb-2">Alamat Singkat</p>
                <p className="text-2xl font-bold text-neon-blue">{shortenAddress(walletAddress)}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-card p-6"
              >
                <p className="text-gray-400 text-sm mb-2">Network</p>
                <p className="text-xl font-bold text-white">{getNetworkName(chainId)}</p>
              </motion.div>
            </div>

            {/* Sign Message Modal */}
            {showSignModal && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6"
              >
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="text-neon-blue" />
                  Tanda Tangani Pesan
                </h3>
                <textarea
                  value={messageToSign}
                  onChange={(e) => setMessageToSign(e.target.value)}
                  className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-3 text-white mb-4 focus:border-neon-blue focus:outline-none"
                  rows={3}
                  placeholder="Masukkan pesan yang ingin ditandatangani..."
                />
                <div className="flex gap-3 mb-4">
                  <button onClick={signMessage} className="btn-neon-green flex-1">
                    Tanda Tangani
                  </button>
                  <button 
                    onClick={() => setShowSignModal(false)} 
                    className="btn-neon flex-1"
                  >
                    Tutup
                  </button>
                </div>
                {signedMessage && (
                  <div className="bg-dark-lighter rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-2">Signature:</p>
                    <code className="text-neon-green text-xs break-all">{signedMessage}</code>
                  </div>
                )}
              </motion.div>
            )}

            {/* Send Transaction Modal */}
            {sendModalOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6"
              >
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Send className="text-neon-green" />
                  Kirim Transaksi
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Alamat Tujuan</label>
                    <input
                      type="text"
                      value={sendAddress}
                      onChange={(e) => setSendAddress(e.target.value)}
                      className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-neon-blue focus:outline-none"
                      placeholder="0x..."
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Jumlah (ETH)</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={sendAmount}
                      onChange={(e) => setSendAmount(e.target.value)}
                      className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-neon-blue focus:outline-none"
                      placeholder="0.0000"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={sendTransaction} className="btn-neon-green flex-1">
                      Kirim Sekarang
                    </button>
                    <button 
                      onClick={() => setSendModalOpen(false)} 
                      className="btn-neon flex-1"
                    >
                      Batal
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Transactions (Demo Data for Showcase) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6"
            >
              <h3 className="text-xl font-bold text-white mb-2">Riwayat Transaksi</h3>
              <p className="text-sm text-gray-400 mb-4">(Data demo untuk showcase - Real transaction history requires blockchain indexer)</p>
              <div className="space-y-3">
                {transactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="bg-dark-lighter p-4 rounded-xl flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.type === 'receive'
                            ? 'bg-neon-green/20'
                            : 'bg-neon-blue/20'
                        }`}
                      >
                        <Send
                          size={18}
                          className={
                            tx.type === 'receive'
                              ? 'text-neon-green rotate-180'
                              : 'text-neon-blue'
                          }
                        />
                      </div>
                      <div>
                        <p className="text-white font-semibold">
                          {tx.type === 'receive' ? 'Terima' : 'Kirim'}
                        </p>
                        <p className="text-sm text-gray-400">
                          {tx.type === 'receive'
                            ? `Dari ${tx.from}`
                            : `Ke ${tx.to}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-bold ${
                          tx.type === 'receive' ? 'text-neon-green' : 'text-neon-blue'
                        }`}
                      >
                        {tx.type === 'receive' ? '+' : '-'}{tx.amount} ETH
                      </p>
                      <p className="text-sm text-gray-400">{tx.date}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Disconnect */}
            <button
              onClick={disconnectWallet}
              className="w-full btn-neon text-red-500 border-red-500 hover:bg-red-500/10"
            >
              Putuskan Koneksi Wallet
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
